/**
 * 수강권 양도 실패를 사람이 읽을 수 있는 문구로 바꾼다.
 *
 * 관용구는 `features/sharedMembership/errors.ts` 와 같다 — 아는 코드만 문구로 바꾸고,
 * 모르는 코드는 null 을 돌려 기존 오류 처리(serverMessage)에 그대로 맡긴다.
 */

export type TicketTransferFailure = {
    status?: number;
    /** ky apiClient 가 구조화 오류 본문을 보존하는 필드. */
    body?: unknown;
    /** 일부 호출부가 res.data 형태로 넘기는 경우. */
    data?: unknown;
    message?: string;
    /** ky 의 TimeoutError, fetch 의 TypeError 등 — 응답 자체가 없을 때 이름으로 구분한다. */
    name?: string;
    serverMessage?: string;
};

export const TICKET_TRANSFER_COUNT_EXCEEDS_MAX_ERROR_CODE = 'TICKET_TRANSFER_COUNT_EXCEEDS_MAX';
export const TICKET_TRANSFER_COACH_NOT_IN_CENTER_ERROR_CODE = 'TICKET_TRANSFER_COACH_NOT_IN_CENTER';

/** 멱등 키 관련 서버 응답(BACKEND_HANDOFF_N §3). */
export const TICKET_TRANSFER_OPERATION_IN_PROGRESS_ERROR_CODE = 'TICKET_TRANSFER_OPERATION_IN_PROGRESS';
export const MEMBERSHIP_TRANSFER_IN_PROGRESS_ERROR_CODE = 'MEMBERSHIP_TRANSFER_IN_PROGRESS';
/** 그 키의 요청이 10분 넘게 「처리 중」에 멈춰 있다. 기다려도 바뀌지 않는다(재시도 불가). */
export const TICKET_TRANSFER_OPERATION_STALE_ERROR_CODE = 'TICKET_TRANSFER_OPERATION_STALE';
export const TICKET_TRANSFER_OPERATION_FAILED_ERROR_CODE = 'TICKET_TRANSFER_OPERATION_FAILED';
export const TICKET_TRANSFER_IDEMPOTENCY_KEY_REUSED_ERROR_CODE = 'TICKET_TRANSFER_IDEMPOTENCY_KEY_REUSED';
export const TICKET_TRANSFER_IDEMPOTENCY_INDEX_MISSING_ERROR_CODE = 'TICKET_TRANSFER_IDEMPOTENCY_INDEX_MISSING';

/**
 * 서버 10/18 H2·I3 — 쓰기 **전에** 거절하는 두 응답(서버 ticketTransfer.service 의 상품 일괄 잠금 안,
 * 사가 기록을 만들기 전). 이 요청은 아무것도 쓰지 않았다.
 *  - 409 `SETTLEMENT_BULK_IN_PROGRESS`: 같은 상품에 정산 분류 일괄 적용·되돌리기가 돌고 있다.
 *  - 503 `TICKET_TRANSFER_SOURCE_READ_FAILED`: 잠금 안에서 원본 수강권을 다시 읽지 못했다.
 */
export const SETTLEMENT_BULK_IN_PROGRESS_ERROR_CODE = 'SETTLEMENT_BULK_IN_PROGRESS';
export const TICKET_TRANSFER_SOURCE_READ_FAILED_ERROR_CODE = 'TICKET_TRANSFER_SOURCE_READ_FAILED';
export const SETTLEMENT_BULK_IN_PROGRESS_MESSAGE = '상품 분류를 적용하는 중이에요. 잠시 후 다시 시도해 주세요.';
export const TICKET_TRANSFER_SOURCE_READ_FAILED_MESSAGE = '수강권 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.';

/** 서버가 그 키의 요청을 10분 넘게 「처리 중」으로 들고 있다(STALE). 결과를 알 수 없다. */
export const TICKET_TRANSFER_STALE_MESSAGE =
    '처리 결과를 확인할 수 없어요. 수강권 잔여를 확인한 뒤 고객센터에 알려 주세요.';

/** 같은 키로 3번 다시 확인해도 결과를 못 받았다. 새 키로 다시 보내지 않는다. */
export const TICKET_TRANSFER_RETRY_EXHAUSTED_MESSAGE =
    '아직 처리 결과를 받지 못했어요. 두 번 처리되지 않게 다시 보내지 않았어요. 잠시 뒤 수강권 잔여를 확인해 주세요.';

/** 응답 유실·5xx. 자동으로 다시 보내지 않고, 잔여를 보게 한 뒤 사용자가 직접 같은 요청을 다시 확인한다. */
export const TICKET_TRANSFER_UNCONFIRMED_MESSAGE =
    '처리됐는지 확인하지 못했어요. 잔여 회차를 확인해 주세요. 줄지 않았다면 「같은 요청 다시 확인」을 눌러 주세요.';

/** 결과를 모르는 요청이 있는 수강권을 이 창에서 다시 제출하려 할 때. */
export const TICKET_TRANSFER_UNRESOLVED_BLOCK_MESSAGE =
    '이 수강권은 앞서 보낸 요청의 결과를 아직 확인하지 못했어요. 창을 닫고 수강권 잔여를 확인한 뒤 다시 시도해 주세요.';

/**
 * 응답을 받지 못한 실패인가(네트워크 끊김·시간 초과). 상태 코드가 없고, 이름·문구가
 * 네트워크 오류로 보일 때만 그렇다고 본다. 센터 열람 모드 차단처럼 요청을 보내지도 않은
 * 오류는 여기서 걸러져 기존 문구를 그대로 쓴다.
 */
export const isTicketTransferNetworkFailure = (
    failure: TicketTransferFailure | null | undefined,
): boolean => {
    if (!failure) return false;
    if (typeof failure.status === 'number' && failure.status > 0) return false;
    const name = typeof failure.name === 'string' ? failure.name : '';
    if (name === 'TimeoutError' || name === 'TypeError' || name === 'AbortError') return true;
    const message = typeof failure.message === 'string' ? failure.message : '';
    return (
        message === 'Network not connected' ||
        /failed to fetch|load failed|networkerror|timed out/i.test(message)
    );
};

const readErrorCode = (payload: unknown): string => {
    if (!payload || typeof payload !== 'object') return '';
    const errorCode = (payload as { errorCode?: unknown }).errorCode;
    return typeof errorCode === 'string' ? errorCode.trim() : '';
};

const readDetails = (failure: TicketTransferFailure | null | undefined): Record<string, unknown> => {
    const payload = failure?.data ?? failure?.body;
    if (!payload || typeof payload !== 'object') return {};
    const details = (payload as { details?: unknown }).details;
    return details && typeof details === 'object' ? (details as Record<string, unknown>) : {};
};

const toCount = (value: unknown): number | null => {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) return null;
    return value;
};

export const getTicketTransferErrorCode = (
    failure: TicketTransferFailure | null | undefined,
): string => readErrorCode(failure?.data ?? failure?.body);

/**
 * 실패로 끝난 요청의 재생(409 `TICKET_TRANSFER_OPERATION_FAILED`)이 알려 주는 되돌리기 결과(서버 0e6bbd95 K1).
 * - 'complete': `details.rollbackComplete === true` — 대상이 없거나 취소됐고 원본 차감도 없음/되돌림. 진짜 깨끗한 실패.
 * - 'incomplete': `rollbackComplete === false` — 깨끗하다고 증명하지 못했다(되돌리지 못함 확정이 아님 — 응답 유실로 보수적 false 가능).
 * - 'unknown': 필드 없음(지금 배포 중인 서버·옛 기록). 막지는 않고 확인을 먼저 권한다.
 * OPERATION_FAILED 가 아니면 null.
 */
export type TicketTransferRollbackState = 'complete' | 'incomplete' | 'unknown';
export const getTicketTransferRollbackState = (
    failure: TicketTransferFailure | null | undefined,
): TicketTransferRollbackState | null => {
    if (getTicketTransferErrorCode(failure) !== TICKET_TRANSFER_OPERATION_FAILED_ERROR_CODE) return null;
    const complete = readDetails(failure).rollbackComplete;
    if (complete === true) return 'complete';
    if (complete === false) return 'incomplete';
    return 'unknown';
};

/** 실패 재생 뒤 「수강권 목록 확인」 상자 문구(최종 검증 P1-a). */
export const TRANSFER_REVIEW_UNKNOWN_NOTICE =
    '앞선 요청이 실패로 끝났지만, 새 수강권이 생기지 않았는지 서버도 알려 주지 못했어요. 회원의 수강권 목록을 먼저 확인해 주세요. 새로 생긴 수강권이 있으면 다시 보내지 말고 고객센터로 알려 주세요.';
export const TRANSFER_REVIEW_INCOMPLETE_NOTICE =
    '앞선 양도가 되돌려졌는지 확인되지 않아요. 회원의 수강권 목록에서 새로 생긴 수강권과 원래 수강권 회차를 확인해 주세요. 새로 생긴 수강권이 있으면 다시 보내지 말고 고객센터로 알려 주세요.';
export const TRANSFER_REVIEW_UNCONFIRMED_NOTICE =
    '앞서 보낸 요청의 결과를 끝내 확인하지 못했어요. 회원의 수강권 목록에서 새로 생긴 수강권과 원래 수강권 회차를 확인해 주세요. 새로 생긴 수강권이 있으면 다시 보내지 말고 고객센터로 알려 주세요.';
/** 상품 분류 적용 겹침으로 보류된 요청을 같은 키로 다시 보내기 전 확인(5회차 지적 4 — 멱등 인덱스 전 서버는 같은 키도 못 막는다). */
export const TRANSFER_REVIEW_BUSY_NOTICE =
    '상품 분류 적용과 겹쳐 처리되지 않은 요청이에요. 드물게 새 수강권이 이미 생겼을 수 있어요. 회원의 수강권 목록을 확인한 뒤 같은 요청으로 다시 보내 주세요. 새로 생긴 수강권이 있으면 다시 보내지 말고 고객센터로 알려 주세요.';
export const TRANSFER_REVIEW_REQUIRED_MESSAGE =
    '「회원의 수강권 목록을 확인했어요」를 체크해야 다시 보낼 수 있어요.';

/** 되돌리기 미완료로 막힌 수강권을 고른 채일 때 버튼 옆 안내. */
export const TICKET_TRANSFER_ROLLBACK_INCOMPLETE_BLOCK_MESSAGE =
    '앞선 양도가 되돌려졌는지 확인되지 않아 이 수강권은 다시 보낼 수 없어요. 수강권 목록을 확인해 주세요.';

export const TICKET_TRANSFER_ROLLBACK_INCOMPLETE_MESSAGE =
    '양도가 중간에 실패했고, 자동으로 되돌려졌는지 확인되지 않아요. 회원의 수강권 목록에서 새로 생긴 수강권과 원래 수강권 회차를 확인한 뒤, 필요하면 고객센터로 알려 주세요.';

/**
 * 409 `TICKET_TRANSFER_COUNT_EXCEEDS_MAX` 의 `details.maxTransferableCount` 를
 * 그대로 문구에 넣는다. 상한은 서버 값이며 화면에서 다시 계산하지 않는다.
 *
 * `details` 를 못 읽는 응답(구버전·프록시 오류)에서는 null 을 돌려 기존
 * serverMessage 문구를 쓰게 한다.
 */
export const getTicketTransferErrorMessage = (
    failure: TicketTransferFailure | null | undefined,
): string | null => {
    if (isTicketTransferNetworkFailure(failure)) {
        // 응답을 못 받았다 = 처리됐는지 모른다. 실패했다고 단정하지 않는다.
        // 「두 번 처리되지 않아요」라고 약속하지 않는다. 서버 인덱스가 준비되지 않은 동안에는
        // 서버가 키를 무시하므로(BACKEND_HANDOFF_N §3) 같은 키로도 두 번 처리될 수 있다.
        return '응답을 받지 못했어요. 처리됐을 수도 있으니 회원 수강권을 새로고침해 확인해 주세요.';
    }
    const errorCode = getTicketTransferErrorCode(failure);
    if (errorCode === SETTLEMENT_BULK_IN_PROGRESS_ERROR_CODE) {
        return SETTLEMENT_BULK_IN_PROGRESS_MESSAGE;
    }
    if (errorCode === TICKET_TRANSFER_SOURCE_READ_FAILED_ERROR_CODE) {
        return TICKET_TRANSFER_SOURCE_READ_FAILED_MESSAGE;
    }
    if (errorCode === TICKET_TRANSFER_OPERATION_IN_PROGRESS_ERROR_CODE) {
        return '앞서 보낸 요청을 아직 처리하고 있어요. 잠시 뒤 내용을 바꾸지 말고 다시 눌러 주세요.';
    }
    if (errorCode === TICKET_TRANSFER_OPERATION_STALE_ERROR_CODE) {
        return TICKET_TRANSFER_STALE_MESSAGE;
    }
    if (errorCode === MEMBERSHIP_TRANSFER_IN_PROGRESS_ERROR_CODE) {
        return '이 수강권의 다른 양도를 처리하고 있어요. 잠시 뒤 다시 눌러 주세요.';
    }
    if (errorCode === TICKET_TRANSFER_OPERATION_FAILED_ERROR_CODE) {
        // 서버가 이 키의 요청을 실패로 끝냈다. 되돌림 결과는 서버가 알려 준 만큼만 말한다(rollbackComplete).
        const rollback = getTicketTransferRollbackState(failure);
        if (rollback === 'complete') return '앞서 보낸 요청은 실패로 끝났고 바뀐 것은 없어요. 다시 시도해 주세요.';
        if (rollback === 'incomplete') return TICKET_TRANSFER_ROLLBACK_INCOMPLETE_MESSAGE;
        return '앞서 보낸 요청이 실패로 끝났어요. 다시 시도하기 전에 새 수강권이 생기지 않았는지 회원의 수강권 목록을 먼저 확인해 주세요.';
    }
    if (errorCode === TICKET_TRANSFER_IDEMPOTENCY_KEY_REUSED_ERROR_CODE) {
        return '요청 정보가 맞지 않아 처리하지 않았어요. 다시 눌러 주세요.';
    }
    if (errorCode === TICKET_TRANSFER_IDEMPOTENCY_INDEX_MISSING_ERROR_CODE) {
        // 지금 서버는 인덱스가 없으면 503 대신 키를 무시하고 201 로 처리한다. 혹시 503 이 오면
        // 요청이 처리 전에 막힌 것이므로 「처리하지 않았어요」가 맞다. 키를 임의로 빼지 않는다.
        return '지금은 서버 준비가 끝나지 않아 처리하지 않았어요. 잠시 뒤 다시 시도하고, 계속되면 고객센터에 알려 주세요.';
    }
    if (errorCode === TICKET_TRANSFER_COACH_NOT_IN_CENTER_ERROR_CODE) {
        // 서버가 saga 를 시작하기 전에 막은 것이라 원본 회차는 그대로다. 그 사실을 알려
        // 관리자가 "반쯤 처리됐나" 걱정하지 않게 한다.
        return '선택한 강사가 이 센터의 활성 강사가 아닙니다. 아무것도 변경되지 않았습니다. 강사 목록을 새로고침한 뒤 다시 선택해주세요.';
    }
    if (errorCode !== TICKET_TRANSFER_COUNT_EXCEEDS_MAX_ERROR_CODE) {
        return null;
    }

    const details = readDetails(failure);
    const max = toCount(details.maxTransferableCount);
    if (max === null) return null;

    const unconsumed = toCount(details.unconsumedFutureCount) ?? 0;
    // 그룹에서 상한이 잔여보다 작아진 경우에만 이유를 덧붙인다.
    const reason = unconsumed > 0 ? `대기 예약 ${unconsumed}건 몫을 남겨야 합니다. ` : '';

    if (max <= 0) {
        return `${reason}지금은 양도할 수 있는 회차가 없습니다. 예약이 정리된 뒤 다시 시도해주세요.`;
    }
    return `${reason}최대 ${max}회까지 양도할 수 있습니다. 양도 회차를 ${max}회 이하로 줄여 다시 시도해주세요.`;
};

/**
 * 상한 초과 오류가 실어 보낸 최신 상한. 입력값을 바로 보정하는 데 쓴다.
 * 읽을 수 없으면 null — 그때는 입력값을 건드리지 않는다.
 */
export const getTicketTransferMaxFromError = (
    failure: TicketTransferFailure | null | undefined,
): number | null => {
    if (getTicketTransferErrorCode(failure) !== TICKET_TRANSFER_COUNT_EXCEEDS_MAX_ERROR_CODE) {
        return null;
    }
    return toCount(readDetails(failure).maxTransferableCount);
};
